import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface ConfettiCelebrationProps {
  isActive: boolean;
  onComplete?: () => void;
  message?: string;
}

const CONFETTI_COLORS = [
  "#FF6B6B", // Red
  "#4ECDC4", // Teal
  "#FFE66D", // Yellow
  "#95E1D3", // Mint
  "#F38181", // Coral
  "#AA96DA", // Purple
  "#FF9F43", // Orange
  "#74B9FF", // Blue
  "#A29BFE", // Lavender
  "#00B894", // Green
  "#E17055", // Terracotta
  "#FDCB6E", // Gold
];

interface ConfettiPiece {
  id: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  rotation: number;
  color: string;
  size: number;
  shape: "rect" | "circle" | "triangle";
  delay: number;
  duration: number;
}

const generateConfetti = (count: number): ConfettiPiece[] => {
  const pieces: ConfettiPiece[] = [];
  
  for (let i = 0; i < count; i++) {
    const side = i < count / 2 ? "left" : "right";
    
    // Start from corners
    const startX = side === "left" ? -5 : 105;
    const startY = 105; // Bottom of screen
    
    // Spray upward and outward with wider spread
    const spreadX = side === "left" 
      ? Math.random() * 80 + 5 // Spread across screen for left corner
      : Math.random() * 80 + 15; // Spread across screen for right corner
    
    const endY = Math.random() * 90 + 5; // End between 5-95% from top
    
    pieces.push({
      id: i,
      startX,
      startY,
      endX: side === "left" ? spreadX : 100 - spreadX + 20,
      endY,
      rotation: Math.random() * 1440 - 720, // More rotations
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: Math.random() * 14 + 6,
      shape: ["rect", "circle", "triangle", "rect", "circle"][Math.floor(Math.random() * 5)] as "rect" | "circle" | "triangle",
      delay: Math.random() * 0.4,
      duration: 1.4 + Math.random() * 0.8,
    });
  }
  
  return pieces;
};

const ConfettiPieceComponent = ({ piece }: { piece: ConfettiPiece }) => {
  const getShape = () => {
    switch (piece.shape) {
      case "circle":
        return { borderRadius: "50%" };
      case "triangle":
        return { 
          clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
          borderRadius: "0"
        };
      default:
        return { borderRadius: "2px" };
    }
  };

  return (
    <motion.div
      initial={{
        left: `${piece.startX}%`,
        top: `${piece.startY}%`,
        rotate: 0,
        opacity: 1,
        scale: 0,
      }}
      animate={{
        left: `${piece.endX}%`,
        top: `${piece.endY}%`,
        rotate: piece.rotation,
        opacity: [0, 1, 1, 1, 0],
        scale: [0, 1.2, 1, 1, 0.5],
      }}
      transition={{
        duration: piece.duration,
        delay: piece.delay,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      className="absolute"
      style={{
        width: piece.size,
        height: piece.size * (piece.shape === "rect" ? 0.6 : 1),
        backgroundColor: piece.color,
        ...getShape(),
      }}
    />
  );
};

const ConfettiCelebration = ({ isActive, onComplete, message = "Gratulacje!" }: ConfettiCelebrationProps) => {
  const [confetti, setConfetti] = useState<ConfettiPiece[]>([]);
  const [showMessage, setShowMessage] = useState(false);
  const hasTriggered = useRef(false);
  
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (isActive && !hasTriggered.current) {
      hasTriggered.current = true;
      
      // Reduce particles: 0 for reduced motion, 30 for mobile, 150 for desktop
      const particleCount = prefersReducedMotion ? 0 : isMobile ? 30 : 150;
      setConfetti(generateConfetti(particleCount));
      setShowMessage(true);
      
      const timer = setTimeout(() => {
        setShowMessage(false);
        setConfetti([]);
        hasTriggered.current = false;
        onComplete?.();
      }, 2500);
      
      return () => {
        clearTimeout(timer);
      };
    }
    
    if (!isActive) {
      hasTriggered.current = false;
    }
  }, [isActive, onComplete, isMobile, prefersReducedMotion]);

  if (!isActive && confetti.length === 0) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] pointer-events-none overflow-hidden">
      {/* Confetti pieces */}
      {confetti.map((piece) => (
        <ConfettiPieceComponent key={piece.id} piece={piece} />
      ))}

      {/* Celebration message */}
      <AnimatePresence>
        {showMessage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ 
              type: "spring", 
              damping: 15, 
              stiffness: 300,
            }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <div className="bg-background/95 backdrop-blur-md rounded-2xl px-10 py-8 shadow-2xl border border-border text-center">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: [0, 1.3, 1], rotate: 0 }}
                transition={{ delay: 0.1, duration: 0.5, type: "spring" }}
                className="text-5xl mb-3"
              >
                🏆
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-2xl font-display font-bold text-foreground mb-2"
              >
                Gratulacje!
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-primary font-semibold text-lg mb-1"
              >
                Kategoria: {message}
              </motion.p>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-muted-foreground text-sm"
              >
                Ukończyłeś wszystkie pytania w tej kategorii!
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    document.body
  );
};

export default ConfettiCelebration;
